import { useId } from 'react';
import { Select, token } from '@bp-files/ui';
import { USERS_COPY } from '@/components/Users/users.copy';
import type { GrantMode, SpaceSummary } from '@/lib/api';

/**
 * One three-state access picker per Active space — PERM-1's scope and PERM-1a's
 * level, in the one control both grant-writing surfaces render.
 *
 * `GrantEditor` (USR-4) and `InviteMemberForm` (USR-2) ask the same question and
 * must not be able to answer it differently. They were already a copied
 * checkbox list before this; a copied *three-state* list, each with its own
 * disabled rule and its own explanatory line, is where the two would quietly
 * diverge — one of them keeping `Read + write` enabled on a observer space
 * long after the other stopped.
 *
 * The legend is a prop because the two surfaces name the group differently: the
 * editor is only about access, and the invite form has this as one field among
 * several ("Link access (optional)").
 *
 * **What this decides is what to offer, never what is allowed.** SEC-5 puts
 * authorization in the API, which stores a `write` level on a observer space
 * rather than refusing it (USR-4) — so this control being wrong would be a
 * usability bug, not a permission one.
 */

/** The three positions. `none` is absence, which is why it is not a `GrantMode`. */
export type Access = 'none' | GrantMode;

export interface SpaceAccessFieldsetProps {
  /** The agency's **Active** spaces. The caller filters; this renders. */
  spaces: readonly SpaceSummary[];
  /** Current selection. A space absent from the map reads as `No access`. */
  chosen: ReadonlyMap<string, GrantMode>;
  onChange: (spaceId: string, access: Access) => void;
  legend: string;
}

/**
 * The three positions, with `Read + write` disabled where the agency itself
 * cannot send (CON-13).
 *
 * **Disabled rather than absent.** Hiding it would make the control two-state on
 * some rows and three-state on others with nothing on screen to explain the
 * difference, and an admin would never learn the level exists or what would
 * unlock it. `Select` renders a disabled option visible and unselectable, which
 * is exactly the affordance that needs.
 */
function accessOptions(observer: boolean) {
  return [
    { value: 'none', label: USERS_COPY.grants.access.none },
    { value: 'read', label: USERS_COPY.grants.access.read },
    { value: 'write', label: USERS_COPY.grants.access.write, disabled: observer },
  ];
}

export function SpaceAccessFieldset({
  spaces,
  chosen,
  onChange,
  legend,
}: SpaceAccessFieldsetProps) {
  const legendId = useId();

  return (
    <fieldset className="flex flex-col gap-2" aria-labelledby={legendId}>
      {/*
        `mb-2` beyond this fieldset's own `gap-2`: the legend and the first
        agency read as cramped at 8px, on the same fix `FilesNav` and
        `SurfaceNav` needed for their own wordmark-to-list gap.
      */}
      <legend id={legendId} className="text-xs font-medium mb-2">
        {legend}
      </legend>

      {spaces.map((space) => {
        // The agency's own ceiling, straight off the space row the caller was
        // already given (CON-13). No extra fetch, and no second predicate:
        // `participation` is the same derivation the API applies, rendered.
        const observer = space.participation === 'observer';

        return (
          <div key={space.id} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span>{space.name}</span>
              <Select
                label={USERS_COPY.grants.access.label(space.name)}
                value={chosen.get(space.id) ?? 'none'}
                onChange={(value) => {
                  onChange(space.id, value as Access);
                }}
                options={accessOptions(observer)}
                className="w-40"
              />
            </div>

            {observer ? (
              <p className="text-xs" style={{ color: token('bp-text-muted') }}>
                {USERS_COPY.grants.access.observer}
              </p>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}
